/**
 * @fileoverview Paper-clip attach button for the chat input.
 *
 * Hidden behind the `fileUpload` feature flag. Renders a ghost icon button
 * that opens the OS file picker via a hidden `<input type="file">`. Selected
 * browser files are handed to the parent as raw `File[]`; validation and the
 * upload lifecycle live in the `useFileUpload` hook, not here.
 */

import React, { useRef } from "react";
import { Paperclip } from "lucide-react";
import { ALLOWED_UPLOAD_FILE_EXTENSIONS } from "@amzn/afxtaco-types";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface FileUploadButtonProps {
  /**
   * Called with the newly-selected browser files whenever the user picks one
   * or more files from the OS picker. Validation happens downstream in the
   * upload hook.
   */
  onFilesSelected: (files: File[]) => void;
  /** Disables the button (e.g. while an execution is in progress). */
  disabled?: boolean;
}

const ACCEPTED_FILE_TYPES = ALLOWED_UPLOAD_FILE_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(",");

/**
 * Renders the paper-clip attach button plus its hidden file input.
 */
export const FileUploadButton = ({
  onFilesSelected,
  disabled = false,
}: FileUploadButtonProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      onFilesSelected(Array.from(fileList));
    }
    // Reset so selecting the same file again still fires `onChange`.
    event.target.value = "";
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClick}
            disabled={disabled}
            aria-label="Attach file"
            className="h-9 w-9 flex-shrink-0 text-muted-foreground hover:text-foreground"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Attach file</p>
        </TooltipContent>
      </Tooltip>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES || undefined}
        onChange={handleChange}
        className="hidden"
        data-testid="file-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
    </>
  );
};

export default FileUploadButton;
